<script setup lang="ts">
/**
 * /affiliate-programs — the hub for the affiliate program's audience pages:
 * the program in one paragraph, then one card per audience. Targets the broad
 * searches ("affiliate program AI", "HR affiliate", "CV affiliate program").
 */
import { AFFILIATE_PROGRAM_PAGES, affiliateProgramParams, localizeAffiliateProgram } from '~/constants/affiliatePrograms'

const { t, locale } = useI18n()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/affiliate-programy', de: '/affiliate-programme', pl: '/programy-afiliacyjne' } })

const params = affiliateProgramParams()
const pages = computed(() => AFFILIATE_PROGRAM_PAGES.map(page => ({ slug: page.slug, icon: page.icon, ...localizeAffiliateProgram(page, locale.value) })))

const faqItems = computed(() => (['what', 'who', 'pay'] as const).map(id => ({
  id,
  question: t(`affiliatePrograms.hub.faq.${id}.q`),
  answer: t(`affiliatePrograms.hub.faq.${id}.a`, params),
})))
const extraNodes = computed(() => [faqPageNode('/affiliate-programs', faqItems.value)])
</script>

<template>
  <MarketingFeaturePage
    :seo-title="t('seo.affiliatePrograms.title', params)"
    :seo-description="t('seo.affiliatePrograms.description', params)"
    path="/affiliate-programs"
    :breadcrumb-title="t('affiliatePrograms.hub.heading')"
    :extra-nodes="extraNodes"
  >
    <div class="sofax">
      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-sm">
          <div class="shell">
            <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <p class="text-lg">{{ t('affiliatePrograms.hub.short', params) }}</p>
              <div class="mt-6 flex flex-wrap gap-3">
                <SofaxBtn :label="t('affiliatePrograms.cta.join')" to="/affiliate" />
                <SofaxBtn :label="t('affiliatePrograms.cta.terms')" variant="outline" to="/affiliate/terms" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-bottom">
          <div class="shell">
            <SofaxSectionHeading
              :eyebrow="t('affiliatePrograms.hub.list.eyebrow')"
              :title="t('affiliatePrograms.hub.list.title')"
              width="max-w-4xl"
            />
            <ul class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
              <li
                v-for="page in pages"
                :key="page.slug"
                class="flex flex-col rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
              >
                <i :class="['bi', page.icon, 'text-2xl text-accent']" aria-hidden="true" />
                <div class="mt-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ page.audience }}</div>
                <h3 class="mt-2 !text-2xl">{{ page.heading }}</h3>
                <p class="mt-3 flex-1 text-base">{{ page.summaryLine }}</p>
                <div class="mt-5">
                  <SofaxArrowLink :to="`/affiliate-programs/${page.slug}`" :label="t('affiliatePrograms.hub.list.cta')" :sr-suffix="page.heading" />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>

    <MarketingFaqSection
      :title="t('affiliatePrograms.page.faqTitle')"
      :items="faqItems"
      name="affiliate-programs-faq"
    />
  </MarketingFeaturePage>
</template>
